import { GoogleGenerativeAI } from "@google/generative-ai";

// Accede a la clave de API desde las variables de entorno de React
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

// Verifica que la API Key esté presente antes de inicializar
if (!API_KEY) {
  throw new Error("La variable de entorno REACT_APP_GEMINI_API_KEY no está definida. Por favor, créala en tu archivo .env");
}
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const genAI = new GoogleGenerativeAI(API_KEY);
const models = [
  'gemini-3.8-flash',
  'gemini-3.6-flash',
  'gemini-3.5-flash-lite',
  'gemini-3.1-flash-lite',
  'gemini-flash-latest'
];

const INITIAL_DELAY = 2000; // 2 segundos
const MAX_DELAY = 32000;   // Máximo 30 segundos

async function generateContentSafe(prompt) {
  let lastError = 'no hay modelos definidos';

  for (let i = 0; i < models.length; i++) {
    const modelName = models[i];

    try {
      console.log(
        `Intentando con modelo: ${modelName} (Intento ${i + 1}/${models.length})`
      );

      const model = genAI.getGenerativeModel({ model: modelName });
      const result = await model.generateContent(prompt);
      const response = await result.response;

      return response.text();

    } catch (error) {
      lastError = error;

      const message = error.message || '';
      const status = error.status ?? error.statusCode;

      console.warn(`Fallo con ${modelName}:`, message);

      // No insistir si el acceso está denegado.
      if (
        status === 403 ||
        /\b403\b|PERMISSION_DENIED/i.test(message)
      ) {
        throw error;
      }

      // No reintentar errores que no se solucionan cambiando de modelo.
      if (
        status === 400 ||
        /\b400\b|INVALID_ARGUMENT/i.test(message)
      ) {
        throw error;
      }

      // Si falló el último modelo, terminar.
      if (i === models.length - 1) {
        break;
      }

      // Backoff exponencial: 2s, 4s, 8s, 16s...
      const delayMs = Math.min(
        INITIAL_DELAY * Math.pow(2, i),
        MAX_DELAY
      );

      console.log(
        `Esperando ${delayMs / 1000}s antes del siguiente fallback...`
      );

      await delay(delayMs);
    }
  }

  if (lastError?.message?.includes('429')) {
    throw new Error(
      'Se alcanzó un límite de solicitudes de Google. Intenta más tarde.'
    );
  }

  throw lastError;
}

/**S
 * Función auxiliar interna para manejar errores y reintentos (Fallback).
 * Si el modelo principal falla, intenta automáticamente con el de respaldo.
 */
async function generateContentSafe2(prompt, repetir = true) {
  try {
    // Intento 1: Modelo Principal
    const model = genAI.getGenerativeModel({ model: models[0] });
    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text();
  } catch (error) {
    console.warn(`⚠️ Fallo con ${models[0]}. Intentando con ${models[1]}...`, error.message);
    
    try {
      // Intento 2: Modelo de Respaldo
      await delay(2000);
      const fallbackModel = genAI.getGenerativeModel({ model: models[1] });
      const result = await fallbackModel.generateContent(prompt);
      const response = await result.response;
      return response.text();
    } catch (fallbackError) {

      console.warn(`⚠️ Fallo con ${models[1]}. Intentando con ${models[2]}...`, error.message);
      
      try {
        // Intento 3: Modelo super generico
        await delay(2000);
        const lastModel = genAI.getGenerativeModel({ model: models[2] });
        const result = await lastModel.generateContent(prompt);
        const response = await result.response;
        return response.text();
      } catch (fallbackError) {
        if (repetir === true) {
          await delay(2000);
          await generateContentSafe(prompt, false);
        } else {
          console.error(`❌ Error crítico: Ambos modelos fallaron.`);
          if (fallbackError?.message && fallbackError.message.includes('429')) {
            throw new Error("Límite de lecturas diario alcanzado por Google. Intenta más tarde.");
          }
          throw fallbackError;
        }
      }
    }
  }
}

/**
 * Genera una interpretación completa para una tirada de oráculo.
 * @param {string} topic - El tema o pregunta del usuario.
 * @param {string} spreadType - El nombre de la tirada (ej: "Estrella de 5 puntas", "3 Cartas").
 * @param {Array} cards - Array de objetos con la info de la carta: { name, positionName, isReversed, meaning (opcional) }.
 * @param {string} lang - Idioma de la respuesta ('es' o 'en').
 * @returns {Promise<string>} - La interpretación generada.
 */
export async function getOracleReading(topic, spreadType, cards, lang = 'es') {
  try {

    const labels = {
      en: {
        position: "Position",
        card: "Card",
        upright: "Upright",
        reversed: "Reversed",
        breaf: "Brief Description",
        keywords: "Keywords"
      },
      es: {
        position: "Posición",
        card: "Carta",
        upright: "Al derecho",
        reversed: "Invertida",
        breaf: "Breve Descripcion",
        keywords: "Palabras clave"
      },
    };

    // Construimos el contexto de las cartas para el prompt
    const t = labels[lang] || labels['es'];

    const cardsContext = cards.map((c, i) => {
      const orientation = c.isReversed ? t.reversed : t.upright;
      
      return `
        ------------------\n
        ${t.card} ${i + 1}\n
        ------------------\n
        ${t.position} ${i + 1} (${c.positionName}): ${t.card} "${c.name}" (${orientation}).\n
        ${t.breaf}: ${c.breaf}.\n
        ${t.keywords}: ${c.keywords?.join(', ')}.\n
        ------------------\n
      `;
    }).join('\n\n\n');

    const largoTexto = { desde: 500, hasta: 800 };

    const promptEs = `
      Actúa como un lector de oráculos místico, sabio y empático.
      El usuario ha realizado una tirada de tipo "${spreadType}" enfocada en el tema: "${topic}".
      
      Aquí están las cartas que salieron en sus respectivas posiciones:

      ${cardsContext}
      
      Por favor, elabora una interpretación detallada que incluya:
      1. **Energía General:** Una breve síntesis de la energía de la tirada respecto al tema.
      2. **Análisis Carta por Carta:** Explica brevemente qué significa cada carta en la posición que salió. Si la carta está invertida, interpreta su significado de bloqueo, desafío o introspección (shadow work).
      3. **Consejo Final:** Una conclusión integradora o consejo de acción.
      
      Usa un tono inspirador, espiritual pero con los pies en la tierra. Usa formato Markdown (negritas, listas) para que sea fácil de leer.

      Por último, asegúrate de que la respuesta sea consisa y no demasiado larga, idealmente entre ${largoTexto.desde} y ${largoTexto.hasta} palabras.
    `;

    const promptEn = `
      Act as a wise, empathetic, and mystical oracle reader.
      The user has done a "${spreadType}" spread regarding the topic: "${topic}".
      
      Here are the cards drawn in their specific positions:

      ${cardsContext}
      
      Please provide a detailed interpretation including:
      1. **General Energy:** A brief synthesis of the reading regarding the topic.
      2. **Card-by-Card Analysis:** Briefly explain what each card means in its specific position. If a card is Reversed, interpret its meaning as a blockage, challenge, or internal shadow work.
      3. **Final Advice:** An integrative conclusion or actionable advice.
      
      Use an inspiring, spiritual yet grounded tone. Use Markdown formatting (bold, lists) for readability.

      Lastly, ensure the response is concise and not overly long, ideally between ${largoTexto.desde} to ${largoTexto.hasta} words.
    `;

    const finalPrompt = lang === 'en' ? promptEn : promptEs;

    // USAMOS LA FUNCIÓN SEGURA EN LUGAR DE LLAMAR DIRECTAMENTE AL MODELO
    const text = await generateContentSafe(finalPrompt);
    return { success: true, text };

  } catch (error) {
    console.error("Error al obtener lectura de oráculo con Gemini:", error);
    if (error?.message?.includes("Límite de lecturas")) {
        return { success: false, text: "Has alcanzado el límite de uso gratuito de la IA por hoy. Por favor confía en tu intuición o intenta mañana." };
    }
    return lang === 'en' 
      ? { success: false, text: "Sorry, the cosmic connection is weak right now. Please try again later." }
      : { success: false, text: "Lo siento, la conexión cósmica es débil en este momento. Por favor intenta interpretar las cartas con tu intuición o prueba más tarde." };
  }
}

/**
 * Procesa una respuesta de usuario para extraer su idea principal usando Gemini,
 * evitando la repetición con base en respuestas anteriores.
 * @param {string} userResponse - El texto completo de la respuesta del usuario.
 * @param {string[]} previousAnswers - Un array con las respuestas anteriores en la misma área.
 * @returns {Promise<string>} - La idea principal, lista para ser usada en una nueva pregunta.
 */
export async function getCoreIdeaFromText(userResponse, previousAnswers = []) {
  if (!userResponse || userResponse.length < 15) {
    return userResponse;
  }
  try {
    // El historial de "porqués" anteriores para dar contexto al modelo.
    const historyContext = previousAnswers
      .filter(ans => ans.trim() !== '')
      .map((ans, index) => `Porqué ${index + 1}: ${ans}`)
      .join('\n');

    const prompt = `La siguiente es la respuesta más reciente en la actividad de "los siete por qué". El objetivo es extraer la idea o el sentimiento clave de esta respuesta para formular la siguiente pregunta.

**Importante**: La idea extraída debe ser diferente a las ideas centrales de las respuestas anteriores para evitar preguntas repetitivas y ayudar al usuario a profundizar de verdad.
Debe ser concisa y apta para ser insertada en la pregunta "¿Por qué es importante para ti <IDEA_PRINCIPAL>?"

${historyContext ? `**Historial de respuestas anteriores:**\n${historyContext}\n` : ''}
**Respuesta más reciente del usuario (de la que debes extraer la nueva idea):**
"${userResponse}"

Analiza la respuesta más reciente y extrae la nueva idea principal o el nuevo sentimiento. La extracción debe ser concisa y sonar natural para insertarla en la pregunta: "Entendido, ¿y por qué es importante para ti <NUEVA_IDEA_EXTRAÍDA>?".
Devuelve solo la <NUEVA_IDEA_EXTRAÍDA> sin el prefijo "Entendido, ¿y por qué es importante para ti" o el signo de interrogación.`;

    const coreIdea = await generateContentSafe(prompt);
    return coreIdea.trim().replace(/^"|"$/g, '');
  } catch (error) {
    console.error("Error al procesar con Gemini (getCoreIdeaFromText):", error);
    // Fallback simple si la API falla
    return userResponse.length > 50 ? userResponse.substring(0, 50) + '...' : userResponse;
  }
}

/**
 * Genera la pregunta inicial de "por qué" para la actividad, de manera directa.
 * @param {string} desire - El deseo inicial del usuario.
 * @returns {Promise<string>} - La pregunta inicial formulada por Gemini.
 */
export async function createQuestionFromDesire(desire) {
  if (!desire) {
    return "¿Por qué es importante para ti lograr esto?";
  }
  try {
    const prompt = `El usuario ha expresado el siguiente deseo: "${desire}".
    Genera una pregunta inicial para la actividad de "los 7 por qué" que indague directamente sobre la importancia de este deseo.
    La pregunta debe comenzar con "¿Por qué es importante para ti...?" y terminar con un signo de interrogación.
    Ejemplo: Si el deseo es "escribir libros", la pregunta podría ser "¿Por qué es importante para ti escribir libros?".`;

    const question = await generateContentSafe(prompt);
    return question.trim();
  } catch (error) {
    console.error("Error al procesar con Gemini (createQuestionFromDesire):", error);
    // Fallback simple si la API falla
    return `¿Por qué es importante para ti ${desire.toLowerCase()}?`;
  }
}

/**
 * Convierte un texto de visión de futuro en una pregunta inicial de "por qué".
 * (Esta función no se usará en el componente SevenWhys, pero se mantiene en el servicio)
 * @param {string} currentText - El texto del estado actual del usuario.
 * @param {string} visionText - El texto completo de la visión de futuro del usuario.
 * @returns {Promise<string>} - Una pregunta natural que indaga por qué esa visión es importante.
 */
export async function createWhyQuestionFromVision(currentText, visionText) {
  if (!visionText) {
    return "¿Por qué es importante para ti alcanzar esta visión?";
  }
  try {
    const prompt = `Los siguientes textos que te voy a pasar representan el estado actual en una area, y la visión ideal con respecto a esa misma area. Convierte esos textos en una pregunta inicial para la actividad de "los 7 por qué", que sirva de pie para luego seguir indagando en razones más profundas de por qué esa persona quiere realmente llegar a donde quiere llegar, que realmente lo motiva. La pregunta empezará con "¿Por qué es importante para ti...?". La pregunta debe ser inspiradora y capturar la esencia de la visión.

Texto de estado actual: "${currentText}"

Texto de visión: "${visionText}"

Pregunta generada:`;
    
    const question = await generateContentSafe(prompt);
    return question.trim();
  } catch (error) {
    console.error("Error al procesar con Gemini (createWhyQuestionFromVision):", error);
    // Fallback simple si la API falla
    return `Has descrito una visión poderosa. ¿Por qué es tan importante para ti hacerla realidad?`;
  }
}